import {
  FileText,
  MagnifyingGlass,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { RadioTab } from 'src/components/RadioTab';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useNotification } from 'src/contexts/NotificationContext';
import {
  Action,
  ActionPayload,
  ActionType,
  TriggerType,
} from 'src/interfaces/action.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { actionConfig, newAction, triggerTypes } from 'src/stubs/action.stub';
import { limitString } from 'src/utils/limitString';
import { processPhoneNumber } from 'src/utils/number';
import { ActionForm } from './components/ActionForm';
import { ActionTypeSelector } from './components/ActionTypeSelector';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can } from 'src/hooks/usePermissions';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { Subject } from 'src/hooks/usePermissions';

const headers = [
  { key: 'label', label: 'Name', width: '30%' },
  { key: 'description', label: 'Description', width: '30%' },
  {
    key: 'action_trigger',
    label: 'Action Trigger',
    width: '30%',
    disableSorting: true,
  },
  {
    key: 'phone_number',
    label: 'Phone Number',
    width: '30%',
    wrapValue: false,
    disableSorting: true,
  },
  { key: 'id', label: 'ID', width: '8rem' },

  { key: 'actions', label: '', width: '20%', disableSorting: true },
];

export const ListActions = () => {
  const { get, patch, post, remove } = useAuthedRequest();
  const [equivalentCodeOpen, setEquivalentCodeOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const notification = useNotification();

  const [isExternal, setIsExternal] = useState(
    searchParams.get('tab') === 'external',
  );

  const [rows, setRows] = useState<Action[]>([]);
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, { wait: 500 });
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState<ActionPayload>(newAction);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });
  const [showActionForm, setShowActionForm] = useState(false);

  const { role } = useEnvironment();

  const fetchData = useCallback(
    async (page: number, perPage: number) => {
      setLoading(true);
      setEquivalentCodeOpen(false);

      const queryObject: QueryObject = {
        page: String(page),
        size: String(perPage),
        filters: JSON.stringify({
          ...(filter.length > 0 && { label: filter }),
          type: isExternal ? 'action_external' : '!action_external',
          scope: 'global|user',
        }),
      };

      if (sortConfig.key) {
        queryObject.sort_column = sortConfig.key;
        queryObject.sort_desc =
          sortConfig.direction === 'desc' ? 'true' : 'false';
      }

      const query = new URLSearchParams(
        queryObject as unknown as Record<string, string>,
      );

      const data = await get(`/actions?${query.toString()}`);

      const items = data.items;

      setRows(items);
      setTotalItems(data.total);
      setLoading(false);
    },
    [currentPage, sortConfig, debouncedFilter, isExternal],
  );

  useEffect(() => {
    fetchData(currentPage, size);
  }, [currentPage, sortConfig, debouncedFilter, isExternal, fetchData, size]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    fetchData(page, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const resetForm = () => {
    setForm(newAction);
  };

  const openModalEditAction = (action: Action) => {
    setModalOpen(true);
    setSelectedId(action.id);

    if (action) {
      setForm({
        ...action,
        ...(action.config.input_schema && {
          config: {
            ...action.config,
            input_schema: JSON.stringify(action.config.input_schema),
          },
        }),
      });
    } else {
      notification.error('Action not found');
    }
  };

  const openDeleteAction = (action: Action) => {
    setModalDeleteOpen(true);
    setSelectedId(action.id);
  };

  const closeModal = () => {
    setEquivalentCodeOpen(false);
    setModalOpen(false);
    setSelectedId('');
    setShowActionForm(false);
    resetForm();
  };

  const submitModal = async (formData: ActionPayload) => {
    setLoading(true);
    const cloneForm = JSON.parse(JSON.stringify(formData)) as ActionPayload;
    if (cloneForm.type === ActionType.External) {
      cloneForm.label = cloneForm.config.name;
      cloneForm.description = cloneForm.config.description;
      if (cloneForm.config.input_schema) {
        try {
          cloneForm.config.input_schema = JSON.parse(
            cloneForm.config.input_schema,
          );
        } catch {
          notification.error('Invalid JSON in input schema');
          setLoading(false);
          return;
        }
      }
    }

    const cleanedConfig = Object.fromEntries(
      Object.entries(cloneForm.config).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );

    const cleanedActionTrigger = Object.fromEntries(
      Object.entries(cloneForm.action_trigger).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          (typeof value !== 'object' || Object.keys(value).length > 0),
      ),
    );

    const cleanedForm = {
      ...cloneForm,
      config: cleanedConfig,
      action_trigger: cleanedActionTrigger,
    };

    if (selectedId) {
      await patch(`/actions/${selectedId}`, {
        ...cleanedForm,
        label: cleanedForm.label || 'Untitled Action',
      });
      notification.success('Action updated successfully');
    } else {
      await post('/actions', {
        ...cleanedForm,
        label: cleanedForm.label || 'Untitled Action',
      });
      notification.success('Action created successfully');
    }

    resetForm();
    fetchData(currentPage, size);
    closeModal();
  };

  const handleRemoveAction = async () => {
    setLoading(true);
    try {
      await remove(`/actions/${selectedId}`);
      fetchData(currentPage, size);
      notification.success('Action deleted successfully');
      setModalDeleteOpen(false);
      setSelectedId('');
    } catch {
      notification.error('Failed to delete action');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (equivalentCodeOpen && !loading) {
      setEquivalentCodeOpen(true);
    }
  }, [form, equivalentCodeOpen, loading]);

  const needsPhoneNumber =
    form.type === ActionType.TransferCall ||
    form.type === ActionType.AddToConference;

  useEffect(() => {
    if (selectedId === '') {
      const resetedForm: ActionPayload = {
        type: form.type,
        config: {},
        action_trigger: {
          type: TriggerType.FunctionCall,
          config: {},
        },
      };

      if (form.type === ActionType.External) {
        resetedForm.config = {
          name: '',
          description: '',
          url: '',
          speak_on_send: false,
          speak_on_receive: false,
          input_schema: '',
          processing_mode: 'muted',
          signature_secret: '',
          account_connection_id: '',
        };
      }

      setForm(resetedForm);
    }
  }, [form.type, selectedId]);

  useEffect(() => {
    if (form.type === ActionType.External && selectedId === '') {
      setForm({
        ...form,
        config: {
          ...form.config,
          speak_on_receive: false,
          speak_on_send: false,
        },
      });
    }
  }, [modalOpen]);

  return (
    <div className="flex-1">
      <Heading
        title="Actions"
        subtitle="Actions give you control in the middle of a conversation, allowing dynamic behavior."
        className="border-b-0"
      >
        <div className="flex">
          <Button
            className="w-42 border-none flex items-center justify-center text-neutral-500"
            variant="outlined"
            color="default"
            href="https://docs.fluents.ai/api-reference/actions"
            target="_blank"
          >
            View help doc
            <FileText className="ml-2" size={18} />
          </Button>
        </div>
      </Heading>

      <RadioTab
        onChange={(index) => {
          setIsExternal(index === 'external');
        }}
        items={[
          { label: 'Phone Actions', value: 'phone' },
          {
            label: 'Custom External Actions',
            value: 'external',
          },
        ]}
        className="pl-2"
        value={isExternal ? 'external' : 'phone'}
      />

      <ContentWrapper>
        <div className="flex flex-col md:flex-row justify-between mb-6">
          <div className="flex w-full md:w-96 self-end">
            <Input
              type="text"
              required
              placeholder="Search actions"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              icon={<MagnifyingGlass size={20} />}
              iconPosition="left"
              inputAction={() => fetchData(currentPage, size)}
            />
          </div>

          <Can operation="create" subject={Subject.ACTIONS}>
            <Button
              className="mt-4 md:mt-0 h-auto"
              onClick={() => setShowActionForm(true)}
            >
              Create Action
            </Button>
          </Can>
        </div>

        <h2 className="mb-4">
          {isExternal ? (
            <div className="flex items-center">
              <img
                src="/assets/actions/modal/custom-external-actions.svg"
                alt="External"
                className="w-6 h-6 mr-2"
              />
              <span>Custom External Actions</span>
            </div>
          ) : (
            'Phone Actions'
          )}
        </h2>

        <Can operation="read" subject={Subject.ACTIONS}>
          <Table
            headers={headers}
            rows={
              rows.map((item: Action) => ({
                ...item,
                id: (
                  <CopyableField
                    className="text-neutral-500"
                    value={item.id}
                    notification={notification}
                  />
                ),
                name: item.label || 'Untitled',
                description: (
                  <div className="text-neutral-500">
                    {limitString(item.description || '', 80)}
                  </div>
                ),
                label: (
                  <div className="flex">
                    {!isExternal && (
                      <div className="w-[50px] h-[50px] bg-neutral-200/25 rounded-lg flex items-center justify-center">
                        <img
                          src={`/assets/actions/modal/${actionConfig[item.type].icon}`}
                          alt={actionConfig[item.type].label}
                          className={clsx(
                            'flex select-none pointer-events-none',
                            actionConfig[item.type].iconStyling,
                          )}
                        />
                      </div>
                    )}
                    <div className="ml-2">
                      {item.label || 'Untitled'}
                      <div className="text-description text-neutral-500 leading-6">
                        {actionConfig[item.type].label}
                      </div>
                    </div>
                  </div>
                ),
                action_trigger: (
                  <div
                    className={clsx(
                      'inline-flex px-3 pt-1.5 pb-1 text-neutral-600 rounded-md',
                      item.action_trigger.type === TriggerType.FunctionCall &&
                        'bg-viridian-100',
                      item.action_trigger.type === TriggerType.PhraseBased &&
                        'bg-primary-100',
                    )}
                  >
                    {triggerTypes.find(
                      (trigger) => trigger.value === item.action_trigger.type,
                    )?.label || item.action_trigger.type}
                  </div>
                ),
                phone_number: (
                  <div className="text-neutral-500">
                    {item.config.phone_number
                      ? processPhoneNumber(item.config.phone_number)
                      : ''}
                  </div>
                ),
                actions: item.user_id && (
                  <div
                    className="flex justify-center items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <SideDropActions
                      options={[
                        {
                          label: 'Edit',
                          Icon: Pencil,
                          onClick: () => openModalEditAction(item),
                          show: can(role, 'update', Subject.ACTIONS),
                        },
                        {
                          label: 'Delete',
                          Icon: Trash,
                          color: 'error',
                          onClick: () => openDeleteAction(item),
                          show: can(role, 'delete', Subject.ACTIONS),
                        },
                      ]}
                    />
                  </div>
                ),
              })) as never[]
            }
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={setSize}
            defaultSize={size}
            onRowClick={(index: number) => {
              const action = rows[index] as unknown as Action;
              if (action && can(role, 'update', Subject.ACTIONS)) {
                openModalEditAction(action);
              }
            }}
          />
        </Can>
      </ContentWrapper>

      <ActionTypeSelector
        isOpen={showActionForm}
        onClose={closeModal}
        onSelect={(type) => {
          setForm({ ...newAction, type });
          setModalOpen(true);
          setShowActionForm(false);
        }}
        selectedType={form.type}
      />

      <ActionForm
        key={form.type + '_' + selectedId}
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={submitModal}
        initialForm={form}
        selectedId={selectedId}
        loading={loading}
        needsPhoneNumber={needsPhoneNumber}
      />

      <Modal
        title="Delete Action"
        isOpen={modalDeleteOpen}
        onClose={() => setModalDeleteOpen(false)}
        actionButton={
          <Button onClick={handleRemoveAction}>Delete Action</Button>
        }
        className="w-96"
      >
        <div className="text-center">
          Are you sure you want to delete this action?
        </div>
      </Modal>
    </div>
  );
};
